import { DateService } from "@/core/services/Date.service";
import { DateAdapter } from "@/infrastructure/adapters/dayJs";
import { LogLevel, Environment as LoggerEnvironment } from "../logger";

export enum Environment {
  DEV = "DEV",
  ALPHA = "ALPHA",
  BETA = "BETA",
  PROD = "PROD",
}

export interface DateConfig {
  DEFAULT_FROM_DATE: string;
  DEFAULT_TO_DATE: string;
  LAST_AVAILABLE_DATE: string;
}

const dateService = new DateService(new DateAdapter());
const currentDate = dateService.getCurrentDate().toISOString();

export class AppConfig {
  private static _instance: AppConfig;

  // Configuration properties
  public ENV: Environment = Environment.PROD;
  public VERSION: string;
  public API_BASE_URL: string;
  public TIMEOUT: number;
  public TOOLTIP_DELAY: number;
  public GRAPHQL_BASE_URL: string;
  public GRAPHQL_TIMEOUT: number;
  public TIMEZONE: string;
  public TIMESTAMP: number;
  public PAGINATION_PAGE_LIMIT: number;
  public DEFAULT_DATE_FORMAT: string;
  public DEFAULT_DATE_FORMAT_WITH_TIME: string;
  public DEFAULT_TIME_FORMAT: string;
  public DEFAULT_NUMBER_FORMAT: string;
  public DEFAULT_FILTER_CHECK_DATE_RANGE: number;
  public HIGHLIGHT_PROJECT_ID: string;
  public HIGHLIGHT_SERVICE_NAME: string;
  public WEBVIEWER_LICENSE_KEY: string;
  public USERBACK_TOKEN: string;
  public INTELLIREVENUE_ASSETS_BASE_URL: string;
  public FILTER_DATE_CONFIG: DateConfig;
  public WEBSOCKET_BASE_URL: string;
  public SENTRY_PROJECT: string;
  public SENTRY_DSN: string;
  public CURRENT_LOGGER: string;
  public CURRENT_FEEDBACK: "USERBACK" | "SENTRY" | "NONE";
  public LOG_LEVEL: LogLevel;
  public SENTRY_ENVIRONMENT: LoggerEnvironment;
  public SENTRY_SERVICE_NAME: string;

  // Private constructor to enforce singleton pattern
  private constructor() {
    this.ENV = import.meta.env.VITE_ENV?.toUpperCase() as Environment;
    this.VERSION = import.meta.env.VITE_VERSION;
    this.API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
    this.GRAPHQL_BASE_URL = import.meta.env.VITE_GRAPHQL_BASE_URL;
    this.TIMEOUT = 1000 * 60 * 5;
    this.TOOLTIP_DELAY = 400;
    this.GRAPHQL_TIMEOUT = 5000;
    this.TIMEZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;
    this.TIMESTAMP = Date.now();
    this.PAGINATION_PAGE_LIMIT = 30;
    this.DEFAULT_DATE_FORMAT = "DD MMM, YYYY";
    this.DEFAULT_DATE_FORMAT_WITH_TIME = "DD MMM, YYYY HH:mm:ss";
    this.DEFAULT_TIME_FORMAT = "HH:mm:ss";
    this.DEFAULT_NUMBER_FORMAT = "accounting";
    this.DEFAULT_FILTER_CHECK_DATE_RANGE = 3;
    this.HIGHLIGHT_PROJECT_ID = import.meta.env.VITE_HIGHLIGHT_PROJECT_ID;
    this.HIGHLIGHT_SERVICE_NAME = "DM-WEB";
    this.WEBVIEWER_LICENSE_KEY = import.meta.env.VITE_WEBVIEWER_LICENSE_KEY;
    this.USERBACK_TOKEN = import.meta.env.VITE_USERBACK_TOKEN;
    this.INTELLIREVENUE_ASSETS_BASE_URL = import.meta.env.VITE_INTELLIREVENUE_ASSETS_BASE_URL;
    this.WEBSOCKET_BASE_URL = import.meta.env.VITE_WEBSOCKET_BASE_URL;
    this.SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;
    this.SENTRY_PROJECT = import.meta.env.VITE_SENTRY_PROJECT;
    this.CURRENT_LOGGER = import.meta.env.VITE_CURRENT_LOGGER;
    this.CURRENT_FEEDBACK = import.meta.env.VITE_CURRENT_FEEDBACK;
    this.LOG_LEVEL = import.meta.env.VITE_LOG_LEVEL;
    this.SENTRY_ENVIRONMENT = import.meta.env.VITE_SENTRY_ENVIRONMENT as LoggerEnvironment;
    this.SENTRY_SERVICE_NAME = import.meta.env.VITE_SENTRY_SERVICE_NAME;
    this.FILTER_DATE_CONFIG = {
      DEFAULT_FROM_DATE: dateService.format(dateService.subtract(currentDate, this.DEFAULT_FILTER_CHECK_DATE_RANGE, "months").toISOString(), "YYYY-MM-DD"),
      DEFAULT_TO_DATE: dateService.format(currentDate, "YYYY-MM-DD"),
      LAST_AVAILABLE_DATE: dateService.format(dateService.subtract(currentDate, 550, "day").toISOString(), "YYYY-MM-DD"),
    };
  }

  // Singleton instance accessor
  public static getInstance(): AppConfig {
    if (!AppConfig._instance) {
      AppConfig._instance = new AppConfig();
    }
    return AppConfig._instance;
  }
}
